import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid, FormControlLabel } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import SydSwitch from '../../../../../commonDesign/SydSwitch'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(1)
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  formControlLabel: {
    '& .MuiFormControlLabel-label': {
      paddingLeft: 'var(--padding-sm)'
    }
  }
}))

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDatasourceAlwaysAvailable } = dataCatalog.useModifyDatasourceAlwaysAvailable()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      isAlwaysAvailable: formData.isAlwaysAvailable
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyDatasourceAlwaysAvailable(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the always available status')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, modifyDatasourceAlwaysAvailable])

  return {
    onSubmit,
    processing,
    error
  }
}

function ModifyDatasourceAlwaysAvailableForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const [isAlwaysAvailable, setIsAlwaysAvailable] = useState(dataSource.isAlwaysAvailable || false)
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.infoText}>
            When enabled, this datasource will always be available regardless of focus restrictions. When disabled, it will be subject to focus restrictions.
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.switchContainer}>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <SydSwitch
                  value={isAlwaysAvailable}
                  onChange={(e, newValue) => setIsAlwaysAvailable(newValue)}
                  disabled={processing}
                />
              }
              label={isAlwaysAvailable ? 'Always Available (Not Subject to Restrictions)' : 'Subject to Focus Restrictions'}
            />
          </div>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ isAlwaysAvailable })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ModifyDatasourceAlwaysAvailableForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    isAlwaysAvailable: PropTypes.bool
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ModifyDatasourceAlwaysAvailableForm
